import { useEffect } from "react";

export const DEFAULT_SEO = {
  title: "Fugu's",
  description: "Fugu's - Your trusted source for fresh fish and seafood",
};

export const PAGE_SEO = {
  home: {
    title: "Fugu's | Fresh Fish & Seafood Delivered",
    description:
      "Order fresh fish, prawns, crab, and seafood online from Fugu's with fast doorstep delivery.",
  },
  about: {
    title: "About Fugu's",
    description:
      "Learn about Fugu's, our commitment to freshness, quality seafood, and trusted local sourcing.",
  },
  contact: {
    title: "Contact Fugu's",
    description:
      "Get in touch with Fugu's for orders, support, and delivery-related assistance.",
  },
  cart: {
    title: "Your Cart | Fugu's",
    description:
      "Review the seafood items in your cart and update quantities before checkout.",
  },
  wishlist: {
    title: "Your Wishlist | Fugu's",
    description:
      "See your saved fish and seafood products and add them to your cart anytime.",
  },
  checkout: {
    title: "Checkout | Fugu's",
    description:
      "Complete your Fugu's order securely with address selection, delivery slots, and payment options.",
  },
  orderSuccess: {
    title: "Order Success | Fugu's",
    description:
      "Your order has been placed successfully. Thank you for shopping with Fugu's!",
  },
  myOrders: {
    title: "My Orders | Fugu's",
    description:
      "View and track all your Fugu's orders, order history, and delivery status.",
  },
  myAccount: {
    title: "My Account | Fugu's",
    description:
      "Manage your Fugu's account, personal information, email, and saved addresses.",
  },
  addressBook: {
    title: "Address Book | Fugu's",
    description:
      "Manage your saved delivery addresses for fast and easy Fugu's checkout.",
  },
  privacyPolicy: {
    title: "Privacy Policy | Fugu's",
    description:
      "Read Fugu's Privacy Policy to understand how we collect, use, and protect your personal information.",
  },
  termsAndConditions: {
    title: "Terms & Conditions | Fugu's",
    description:
      "Review Fugu's Terms & Conditions covering orders, payments, delivery, cancellations, and refunds.",
  },
};

const ensureMetaTag = (name) => {
  let tag = document.querySelector(`meta[name="${name}"]`);
  let created = false;

  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", name);
    document.head.appendChild(tag);
    created = true;
  }

  return { tag, created };
};

export const sanitizeMetaText = (value, maxLength = 160) => {
  if (!value) return "";

  const cleaned = String(value)
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!cleaned) return "";
  if (cleaned.length <= maxLength) return cleaned;
  return `${cleaned.slice(0, maxLength - 1).trim()}...`;
};

export const buildProductSeo = (productDetails, activeVariant) => {
  const title = sanitizeMetaText(activeVariant?.title, 70) || DEFAULT_SEO.title;
  const description =
    sanitizeMetaText(productDetails?.description, 160) ||
    DEFAULT_SEO.description;

  return { title, description };
};

export const useSeo = ({ title, description }) => {
  useEffect(() => {
    const previousTitle = document.title;
    const previousDescriptionTag = document.querySelector(
      'meta[name="description"]',
    );
    const previousDescription = previousDescriptionTag?.getAttribute("content");
    const { tag: descriptionTag, created } = ensureMetaTag("description");

    document.title = title || DEFAULT_SEO.title;
    descriptionTag.setAttribute(
      "content",
      description || DEFAULT_SEO.description,
    );

    return () => {
      document.title = previousTitle || DEFAULT_SEO.title;

      if (previousDescriptionTag) {
        previousDescriptionTag.setAttribute(
          "content",
          previousDescription || DEFAULT_SEO.description,
        );
      } else if (created) {
        descriptionTag.remove();
      }
    };
  }, [title, description]);
};

export const getPageSeo = (pageKey) => PAGE_SEO[pageKey] || DEFAULT_SEO;

export const usePageSeo = (pageKey) => {
  useSeo(getPageSeo(pageKey));
};
